<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref, toRefs } from 'vue';
import AutoComplete, { AutoCompleteCompleteEvent } from 'primevue/autocomplete';
import Chip from 'primevue/chip';
import { useToast } from 'primevue/usetoast';
import OrganizationContractClincianPanel from './OrganizationContractClinicianPanel.vue';
import { useClinicianOrganizationStore, useOrganizationContractsStore } from '@/store';
import { ClinicianContractDetails, ContractClinicianInterface, ClinicianInterface } from '@/types';

interface Props {
  clinicians: ClinicianContractDetails[];
}

const props = defineProps<Props>();
const { clinicians } = toRefs(props);
const contractStore = useOrganizationContractsStore();
const organizationStore = useClinicianOrganizationStore();
const { loading } = storeToRefs(contractStore);
const canEdit = contractStore.canEditOrCreateContracts;
const toast = useToast();
const newClinician = ref();
const cliniciansAvailableToAdd = ref<ClinicianInterface[]>([]);
const clinicianSearchSuggestions = ref<ClinicianInterface[]>([]);
const clinicianPanels = ref<Node>();
// eslint-disable-next-line
// @ts-ignore
const clinicianForApi: ClinicianContractDetails = {
  contract_id: contractStore.contract.id as number,
  hours: 0,
  person_id: 0,
  person: undefined,
  rate: 0,
  rate_is_hourly: 1,
};
const observerOptions = {
  childList: true,
  subtree: true,
};

const callback = (mutationList: MutationRecord[]) => {
  for (const mutation of mutationList) {
    if (mutation.type === 'childList') {
      // when a new clinician is added, it isn't obvious that they have been added becuase the viewport
      // is hiding the new addition. To help with this, I want to scroll to the bottom clinician panel.
      if (mutation.addedNodes.length && mutation.addedNodes[0].nodeName == 'DIV') {
        setTimeout(() => {
          (mutation.addedNodes[0] as Element)?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
        }, 250);
      }
    }
  }
};

const observer = new MutationObserver(callback);

onMounted(async () => {
  await getClinicians();
  if (clinicianPanels.value) {
    observer.observe(clinicianPanels.value as Node, observerOptions);
  }
});

const getClinicians = async () => {
  if (contractStore.organizationId) {
    await organizationStore.getCliniciansForOrganizationRequest(contractStore.organizationId);
    const availableClinicians = await organizationStore.getCliniciansAvailableToAddToContract(
      contractStore.organizationId,
    );
    cliniciansAvailableToAdd.value = [...new Set(availableClinicians.concat(organizationStore.clinicians))];
  }
};

/**
 * filter the clinicians out of the dropdown who are not already in the contract
 */
const cliniciansNotInContract = computed(() => {
  return cliniciansAvailableToAdd.value.filter((orgClinician) => {
    let found = false;
    clinicians.value.forEach((clinician) => {
      if (orgClinician.id === clinician?.person?.id) {
        found = true;
      }
    });
    if (!found) return orgClinician;
  });
});

const activeClinicians = computed(() => clinicians.value.filter((item) => !item.person?.archived_at));

const totalCliniciansOnContract = computed<number>(() => activeClinicians.value.length);

const doToast = (summary: string, detail: string) => {
  toast.add({
    severity: 'success',
    summary,
    detail,
    life: 3000,
  });
};

const addServiceProvider = async () => {
  if (newClinician.value) {
    if (contractStore.serviceProviderIsNotInAnotherContract(newClinician.value.id)) {
      clinicianForApi.person_id = newClinician.value.id;
      clinicianForApi.id = newClinician.value.id;
      clinicianForApi.person = newClinician.value;
      contractStore.addServiceProviderToPendingRequests({ ...clinicianForApi } as ContractClinicianInterface);
      resetForm();
    } else {
      contractStore.doToast('This Service Provider cannot be added to this contract', 'Cannot Be Added', true, 5000);
      newClinician.value = null;
    }
  }
};

const searchClinicians = (event: AutoCompleteCompleteEvent) => {
  const searchString = event.query.trim();
  if (!searchString.length) {
    clinicianSearchSuggestions.value = [...cliniciansNotInContract.value];
  } else {
    clinicianSearchSuggestions.value = cliniciansNotInContract.value.filter((clinician: ClinicianInterface) => {
      return clinician.display_name?.toLowerCase().includes(searchString.toLowerCase());
    });
  }
};

/**
 * reset the current newClinician,throw toast, call the api for updated contract
 */
const resetForm = () => {
  newClinician.value = null;
  doToast('Service Provider added', 'Success');
};
</script>

<template>
  <div class="grid m-0 pt-5 px-4 pb-5">
    <div class="flex w-full justify-content-between">
      <div class="col-12 md:col-8 lg:col-6">
        <h3 class="mt-2 p-0 font-bold">
          <span>Service Providers</span>
          <Chip class="ml-3">{{ totalCliniciansOnContract }}</Chip>
        </h3>
      </div>
      <div v-if="canEdit" class="col-12 md:col-8 lg:col-6">
        <IconField icon-position="left">
          <InputIcon class="p-input-icon-left" style="z-index: 100">
            <Icon name="search" class="text-lg" />
          </InputIcon>
          <AutoComplete
            id="service_providers"
            v-model="newClinician"
            class="multiselect-custom w-full"
            input-class="w-full pl-5"
            :multiple="false"
            option-label="display_name"
            placeholder="Add Service Providers"
            dropdown-icon="pi pi-search"
            :suggestions="clinicianSearchSuggestions"
            @item-select="addServiceProvider"
            @complete="searchClinicians($event)"
          />
        </IconField>
      </div>
    </div>
    <div ref="clinicianPanels" class="w-full">
      <OrganizationContractClincianPanel
        v-for="clinician in activeClinicians"
        :key="clinician.id"
        :loading="loading"
        :clinician="clinician"
        :just-added="clinician.toBeAddedToContract"
      />
    </div>
  </div>
</template>
